import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Empty, Form, Input, Modal, Popconfirm, Table, Tooltip } from 'antd'
import Button from 'components/Button'
import Icon from 'components/Icon'
import useClient from 'hooks/useClient'
import { ICONS, PERMISSIONS } from 'utils/constants'
import {
  createFaqCategory,
  createFaqItem,
  deleteFaqCategory,
  deleteFaqItem,
  getFaqCategories,
  updateFaqCategory,
  updateFaqItem,
} from 'redux/actions/faq'
import './Faq.scss'

const { TextArea } = Input

const Faq = () => {
  const dispatch = useDispatch()
  const { isClientCan } = useClient()

  const canRead = isClientCan(PERMISSIONS.READ_FAQ)
  const canCreate = isClientCan(PERMISSIONS.CREATE_FAQ)
  const canUpdate = isClientCan(PERMISSIONS.UPDATE_FAQ)
  const canDelete = isClientCan(PERMISSIONS.DELETE_FAQ)

  const { categories = [], isGetLoading, isChangeLoading } = useSelector((state) => state.faq)

  const [categoryModalVisible, setCategoryModalVisible] = useState(false)
  const [itemModalVisible, setItemModalVisible] = useState(false)
  const [activeCategoryId, setActiveCategoryId] = useState(null)
  const [expandedCategoryKey, setExpandedCategoryKey] = useState(null)
  const [editingCategory, setEditingCategory] = useState(null)
  const [editingItem, setEditingItem] = useState(null)

  const [categoryForm] = Form.useForm()
  const [itemForm] = Form.useForm()

  const activeCategory = useMemo(
    () => categories.find((category) => category.id === activeCategoryId) || null,
    [categories, activeCategoryId],
  )

  useEffect(() => {
    if (canRead) {
      dispatch(getFaqCategories())
    }
  }, [canRead, dispatch])

  const openCreateCategoryModal = () => {
    setEditingCategory(null)
    categoryForm.resetFields()
    setCategoryModalVisible(true)
  }

  const openEditCategoryModal = useCallback(
    (category) => {
      setEditingCategory(category)
      categoryForm.setFieldsValue({
        nameEn: category.nameEn || category.name || '',
        nameAr: category.nameAr || '',
      })
      setCategoryModalVisible(true)
    },
    [categoryForm],
  )

  const closeCategoryModal = () => {
    setCategoryModalVisible(false)
    setEditingCategory(null)
    categoryForm.resetFields()
  }

  const submitCategory = async () => {
    const values = await categoryForm.validateFields()
    const payload = {
      name: values.nameEn,
      nameEn: values.nameEn,
      nameAr: values.nameAr,
    }

    if (editingCategory?.id) {
      dispatch(
        updateFaqCategory({
          id: editingCategory.id,
          data: payload,
          onSuccess: closeCategoryModal,
        }),
      )
      return
    }

    dispatch(
      createFaqCategory({
        data: {
          ...payload,
          categoryElements: [],
        },
        onSuccess: closeCategoryModal,
      }),
    )
  }

  const openCreateItemModal = useCallback(
    (categoryId) => {
      setActiveCategoryId(categoryId)
      setEditingItem(null)
      itemForm.resetFields()
      setItemModalVisible(true)
    },
    [itemForm],
  )

  const openEditItemModal = useCallback(
    (categoryId, item) => {
      setActiveCategoryId(categoryId)
      setEditingItem(item)
      itemForm.setFieldsValue({
        questionNameEn: item.questionNameEn || item.questionName || '',
        questionNameAr: item.questionNameAr || '',
        answerTitleEn: item.answerTitleEn || item.answerName || '',
        answerTitleAr: item.answerTitleAr || '',
        answerDescriptionEn: item.answerDescriptionEn || item.answerContent || '',
        answerDescriptionAr: item.answerDescriptionAr || '',
      })
      setItemModalVisible(true)
    },
    [itemForm],
  )

  const closeItemModal = () => {
    setItemModalVisible(false)
    setEditingItem(null)
    itemForm.resetFields()
  }

  const submitItem = async () => {
    const values = await itemForm.validateFields()

    if (!activeCategoryId) return

    if (editingItem?.id) {
      dispatch(
        updateFaqItem({
          categoryId: activeCategoryId,
          itemId: editingItem.id,
          data: values,
          onSuccess: closeItemModal,
        }),
      )
      return
    }

    dispatch(
      createFaqItem({
        categoryId: activeCategoryId,
        data: {
          question: {
            nameEn: values.questionNameEn,
            nameAr: values.questionNameAr,
          },
          answer: {
            titleEn: values.answerTitleEn,
            titleAr: values.answerTitleAr,
            descriptionEn: values.answerDescriptionEn,
            descriptionAr: values.answerDescriptionAr,
          },
        },
        onSuccess: closeItemModal,
      }),
    )
  }

  const getItemColumns = useCallback(
    (categoryId) => [
      {
        title: '№',
        key: 'order',
        width: 56,
        align: 'center',
        render: (_, __, index) => index + 1,
      },
      {
        title: 'Question (EN)',
        dataIndex: 'questionNameEn',
        key: 'questionNameEn',
        render: (value, record) => (
          <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {value || record.questionName || '-'}
          </span>
        ),
      },
      {
        title: 'Question (AR)',
        dataIndex: 'questionNameAr',
        key: 'questionNameAr',
        ellipsis: true,
        render: (value) => (
          <span
            dir="rtl"
            style={{
              textAlign: 'right',
              display: 'inline-block',
              width: '100%',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            {value || '-'}
          </span>
        ),
      },
      {
        title: 'Answer Title (EN)',
        dataIndex: 'answerTitleEn',
        key: 'answerTitleEn',
        render: (value, record) => (
          <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {value || record.answerName || '-'}
          </span>
        ),
      },
      {
        title: 'Answer Title (AR)',
        dataIndex: 'answerTitleAr',
        key: 'answerTitleAr',
        ellipsis: true,
        render: (value) => (
          <span
            dir="rtl"
            style={{
              textAlign: 'right',
              display: 'inline-block',
              width: '100%',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            {value || '-'}
          </span>
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        align: 'center',
        width: 100,
        render: (_, record) => (
          <div className="table-actions">
            {canUpdate && (
              <Tooltip title="Edit FAQ">
                <button
                  className="action-btn action-icon--edit"
                  onClick={(event) => {
                    event.stopPropagation()
                    openEditItemModal(categoryId, record)
                  }}
                >
                  <Icon className="action-icon" icon={ICONS.EDIT} size={20} />
                </button>
              </Tooltip>
            )}
            {canDelete && (
              <Popconfirm
                title="Delete this FAQ item?"
                onConfirm={() => dispatch(deleteFaqItem({ categoryId, itemId: record.id }))}
              >
                <button
                  className="action-btn action-icon--delete"
                  onClick={(event) => event.stopPropagation()}
                >
                  <Icon className="action-icon" icon={ICONS.TRASH} size={20} />
                </button>
              </Popconfirm>
            )}
          </div>
        ),
      },
    ],
    [canDelete, canUpdate, dispatch, openEditItemModal],
  )

  const categoryColumns = useMemo(
    () => [
      {
        title: '№',
        key: 'order',
        width: 70,
        align: 'center',
        render: (_, __, index) => index + 1,
      },
      {
        title: 'Category Name (EN)',
        dataIndex: 'nameEn',
        key: 'nameEn',
        width: '32%',
        render: (value, record) => value || record.name || '-',
      },
      {
        title: 'Category Name (AR)',
        dataIndex: 'nameAr',
        key: 'nameAr',
        width: '32%',
        render: (value) => (
          <span dir="rtl" style={{ textAlign: 'right', display: 'inline-block', width: '100%' }}>
            {value || '-'}
          </span>
        ),
      },
      {
        title: 'FAQs Count',
        key: 'itemsCount',
        width: 120,
        align: 'center',
        render: (_, record) => record.categoryQuestionsAnswers?.length || 0,
      },
      {
        title: 'Actions',
        key: 'actions',
        align: 'center',
        width: 160,
        render: (_, record) => (
          <div className="table-actions">
            {canUpdate && (
              <Tooltip title="Add FAQ">
                <button
                  className="action-btn action-icon--edit"
                  onClick={(event) => {
                    event.stopPropagation()
                    openCreateItemModal(record.id)
                  }}
                >
                  <Icon className="action-icon" icon={ICONS.PLUS} size={20} />
                </button>
              </Tooltip>
            )}
            {canUpdate && (
              <Tooltip title="Edit Category">
                <button
                  className="action-btn action-icon--edit"
                  onClick={(event) => {
                    event.stopPropagation()
                    openEditCategoryModal(record)
                  }}
                >
                  <Icon className="action-icon" icon={ICONS.EDIT} size={20} />
                </button>
              </Tooltip>
            )}
            {canDelete && (
              <Popconfirm
                title="Delete this category?"
                onConfirm={() => dispatch(deleteFaqCategory({ id: record.id }))}
              >
                <button
                  className="action-btn action-icon--delete"
                  onClick={(event) => event.stopPropagation()}
                >
                  <Icon className="action-icon" icon={ICONS.TRASH} size={20} />
                </button>
              </Popconfirm>
            )}
          </div>
        ),
      },
    ],
    [canDelete, canUpdate, dispatch, openCreateItemModal, openEditCategoryModal],
  )

  if (!canRead) {
    return <Empty description="You do not have permission to view FAQ" />
  }

  return (
    <div className="page">
      <div className="page-header">
        <h3 className="page-title">FAQ Categories</h3>
        <div className="page-header-actions">
          {canCreate && (
            <Button type="primary" onClick={openCreateCategoryModal}>
              Add Category
            </Button>
          )}
        </div>
      </div>

      <div className="faq-scroll-area">
      <Table
        className="faq-categories-table"
        rowKey="id"
        tableLayout="fixed"
        columns={categoryColumns}
        dataSource={categories}
        loading={isGetLoading}
        pagination={false}
        locale={{ emptyText: 'No FAQ categories yet' }}
        expandable={{
          expandedRowKeys: expandedCategoryKey ? [expandedCategoryKey] : [],
          onExpand: (expanded, record) => {
            if (!expanded) {
              setExpandedCategoryKey(null)
              return
            }
            setExpandedCategoryKey(record.id)
            setActiveCategoryId(record.id)
          },
          expandedRowRender: (category) => (
            <div style={{ padding: 12, background: '#fafafa' }}>
              <div
                style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                {canUpdate && (
                  <Button type="primary" onClick={() => openCreateItemModal(category.id)}>
                    Add FAQ
                  </Button>
                )}
              </div>
              <Table
                className="faq-expanded-table"
                rowKey="id"
                size="small"
                tableLayout="fixed"
                columns={getItemColumns(category.id)}
                dataSource={category.categoryQuestionsAnswers || []}
                pagination={false}
                locale={{ emptyText: 'No FAQ items in this category' }}
              />
            </div>
          ),
        }}
      />
      </div>

      <Modal
        title={editingCategory ? 'Edit Category' : 'Create Category'}
        visible={categoryModalVisible}
        onCancel={closeCategoryModal}
        onOk={submitCategory}
        confirmLoading={isChangeLoading}
      >
        <Form layout="vertical" form={categoryForm}>
          <Form.Item
            name="nameEn"
            label="Category Name (EN)"
            rules={[{ required: true, message: 'English name is required' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="nameAr"
            label="Category Name (AR)"
            rules={[{ required: true, message: 'Arabic name is required' }]}
          >
            <Input dir="rtl" style={{ textAlign: 'right' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingItem ? 'Edit FAQ Item' : `Create FAQ Item${activeCategory ? ` - ${activeCategory.nameEn || activeCategory.name || ''}` : ''}`}
        visible={itemModalVisible}
        onCancel={closeItemModal}
        onOk={submitItem}
        confirmLoading={isChangeLoading}
        width={800}
      >
        <Form layout="vertical" form={itemForm}>
          <Form.Item
            name="questionNameEn"
            label="Question (EN)"
            rules={[{ required: true, message: 'Question in English is required' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="questionNameAr"
            label="Question (AR)"
            rules={[{ required: true, message: 'Question in Arabic is required' }]}
          >
            <Input dir="rtl" style={{ textAlign: 'right' }} />
          </Form.Item>
          <Form.Item
            name="answerTitleEn"
            label="Answer Title (EN)"
            rules={[{ required: true, message: 'Answer title in English is required' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="answerTitleAr"
            label="Answer Title (AR)"
            rules={[{ required: true, message: 'Answer title in Arabic is required' }]}
          >
            <Input dir="rtl" style={{ textAlign: 'right' }} />
          </Form.Item>
          <Form.Item
            name="answerDescriptionEn"
            label="Answer Description (EN)"
            rules={[{ required: true, message: 'Answer description in English is required' }]}
          >
            <TextArea rows={4} />
          </Form.Item>
          <Form.Item
            name="answerDescriptionAr"
            label="Answer Description (AR)"
            rules={[{ required: true, message: 'Answer description in Arabic is required' }]}
          >
            <TextArea rows={4} dir="rtl" style={{ textAlign: 'right' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default Faq
